import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard,
  Bitcoin,
  X,
  Shield,
  Check,
  AlertCircle,
  Loader
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useLanguage } from '../LanguageContext';
// Active payment methods only — UPay (primary) + Crypto.
// Stripe / PayPal / PayPlus components remain in repo but are no longer wired up.
import CryptoPayment from './CryptoPayment';
import UpayPayment from './UpayPayment';
import { useAuth } from '../../firebase/AuthContext';

export default function PaymentModal({ isOpen, onClose, selectedPlan, onSuccess }) {
  const { t, language, isRTL } = useLanguage();
  const { refreshCreditsFromBackend } = useAuth();
  const [selectedMethod, setSelectedMethod] = useState('upay');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentStep, setPaymentStep] = useState('method'); // method, processing, success

  const paymentMethods = [
    {
      id: 'upay',
      name: language === 'he' ? 'אשראי / Bit (UPay)' : 'Credit Card / Bit (UPay)',
      icon: CreditCard,
      description: language === 'he' ? 'ויזה, ישראכרט, מאסטרקארד, ביט · ₪ ILS' : 'Visa, Mastercard, Israeli cards, Bit · ₪ ILS',
      supported: ['upay'],
      recommended: true,
    },
    {
      id: 'crypto',
      name: t('cryptocurrency'),
      icon: Bitcoin,
      description: language === 'he' ? 'ביטקוין, אתריום, יציב מטבעות' : 'BTC, ETH, USDT',
      supported: ['crypto']
    }
  ];

  const handlePaymentSuccess = async (result) => {
    setIsProcessing(false);
    setPaymentStep('success');
    // CRITICAL: pull latest credits from SQLite (where referral bonus + purchase land)
    // and mirror to Firestore so the UI shows the new balance immediately.
    try {
      await refreshCreditsFromBackend();
    } catch { /* best-effort */ }
    setTimeout(() => {
      onSuccess(result);
      handleClose();
    }, 2000);
  };

  const handlePaymentError = (error) => {
    setIsProcessing(false);
    setError(error.message || t('paymentFailed'));
  };

  const handleClose = () => {
    setPaymentStep('method');
    setSelectedMethod('upay');
    setError(null);
    setIsProcessing(false);
    onClose();
  };

  const handleMethodSelect = (methodId) => {
    setSelectedMethod(methodId);
    setError(null);
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    setError(null);
    setPaymentStep('form');
  };

  const renderPaymentMethodSelector = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white mb-4">
        {t('selectPaymentMethod')}
      </h3>
      
      <div className="grid gap-3">
        {paymentMethods.map((method) => {
          const Icon = method.icon;
          return (
            <motion.div
              key={method.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Card
                className={`cursor-pointer transition-all border-2 ${
                  selectedMethod === method.id
                    ? 'border-violet-500 bg-violet-500/10'
                    : 'border-slate-700 bg-slate-900 hover:border-slate-600'
                }`}
                onClick={() => handleMethodSelect(method.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className={`p-2 rounded-lg ${
                      selectedMethod === method.id
                        ? 'bg-violet-500 text-white'
                        : 'bg-slate-800 text-slate-400'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        {method.recommended && (
                          <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-semibold">
                            {language === 'he' ? 'מומלץ' : 'RECOMMENDED'}
                          </span>
                        )}
                        <h4 className="font-medium text-white">
                          {method.name}
                        </h4>
                      </div>
                      <p className="text-sm text-slate-400">
                        {method.description}
                      </p>
                    </div>
                    {selectedMethod === method.id && (
                      <Check className="w-5 h-5 text-violet-500" />
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      <div className="flex items-center space-x-2 space-x-reverse mt-6 p-3 bg-slate-800/50 rounded-lg">
        <Shield className="w-4 h-4 text-green-500" />
        <span className="text-sm text-slate-300">
          {t('securePayment')} • SSL Encrypted
        </span>
      </div>

      <div className="flex gap-3 mt-6">
        <Button
          variant="outline"
          onClick={handleClose}
          className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </Button>
        <Button
          onClick={handlePayment}
          disabled={!selectedMethod || isProcessing}
          className="flex-1 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700"
        >
          {t('completePurchase')}
        </Button>
      </div>
    </div>
  );

  const renderProcessing = () => (
    <div className="text-center py-8">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-500/20 rounded-full mb-4">
        <Loader className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">
        {t('processingPayment')}
      </h3>
      <p className="text-slate-400">
        Please wait while we process your payment...
      </p>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-8">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
        <Check className="w-8 h-8 text-green-500" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">
        {t('paymentSuccess')}
      </h3>
      <p className="text-slate-400">
        {(selectedPlan?.totalCredits ?? selectedPlan?.credits) || 0} {t('creditsAdded')}
      </p>
    </div>
  );

  const renderPaymentForm = () => {
    switch (selectedMethod) {
      case 'upay':
        return (
          <UpayPayment
            plan={selectedPlan}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            onCancel={handleClose}
          />
        );
      case 'crypto':
        return (
          <CryptoPayment
            plan={selectedPlan}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            onCancel={handleClose}
          />
        );
      default:
        return renderPaymentMethodSelector();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            onClick={handleClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl my-4 max-h-[calc(100dvh-2rem)] flex flex-col"
          >
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-800 shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-white">
                {selectedPlan?.name || 'Purchase Credits'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="text-slate-400 hover:text-white hover:bg-slate-800 w-10 h-10"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto flex-1" style={{ WebkitOverflowScrolling: 'touch' }}>
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center space-x-2 space-x-reverse">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-500">{error}</span>
                </div>
              )}

              {paymentStep === 'method' && renderPaymentMethodSelector()}
              {paymentStep === 'processing' && renderProcessing()}
              {paymentStep === 'success' && renderSuccess()}
              {paymentStep === 'form' && renderPaymentForm()}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
